/**
 * The cursor claim, and who else is holding what (TASK-050).
 *
 * The soft lock has existed server-side since the concurrency model was
 * decided (DECISIONS S15) and has never been engaged, because nothing in the
 * browser claimed a block: "Implemented server-side, and never engaged: no
 * client claims a block." This is the client that claims one.
 *
 * ==================================================================
 * WHAT THE LOCK IS, AND IS NOT
 * ==================================================================
 * It is ADVISORY and it EXPIRES. A held block refuses writes from anybody
 * else -- agent or person -- with `being_edited`, for 20 seconds at a time,
 * renewed while a cursor sits in it. It is not a transaction and it is not a
 * guarantee: if Redis is unreachable the server chooses availability and the
 * lock simply does not apply. What stops two writes colliding in that case is
 * the hash guard, which lives in Postgres and does not fail open.
 *
 * So this file holds a lock the way you hold a door for somebody: politely,
 * and not for longer than you are standing there.
 *
 * ==================================================================
 * WHY THE RENEWAL IS FASTER THAN THE EXPIRY
 * ==================================================================
 * The server's TTL is 20 seconds. Renewing every 8 gives two chances to miss
 * before anybody else can take the block -- a dropped request or a laptop
 * lid does not hand your paragraph to an agent mid-sentence. Renewing at 19
 * would make every slow request a lost lock.
 */
import { api } from '../api'

/** Server TTL is 20s; renew with room for one lost request. See above. */
const RENEW_MS = 8_000
/** How often to ask who else is holding something, when not editing. */
const POLL_MS = 15_000

interface PresenceReply {
  ok?: boolean
  locks?: Record<string, string>
}

export class BlockLocks {
  /** blockId -> the display name of whoever is holding it. Never me. */
  held = $state<Record<string, string>>({})
  /** The block this browser is holding, if any. */
  mine = $state<string | null>(null)

  #nodeId: string | null = null
  #timer: ReturnType<typeof setInterval> | null = null
  #stopped = false

  /** Point at a node. Changing node drops whatever was held on the old one. */
  attach(nodeId: string | null) {
    if (this.#nodeId === nodeId) return
    void this.release()
    this.#nodeId = nodeId
    this.held = {}
    this.#restart()
  }

  #restart() {
    if (this.#timer) clearInterval(this.#timer)
    this.#timer = null
    if (!this.#nodeId || this.#stopped) return
    // Fast while holding something, slow while only watching.
    const every = this.mine ? RENEW_MS : POLL_MS
    this.#timer = setInterval(() => void this.#touch(), every)
    void this.#touch()
  }

  /**
   * One round trip that does three things: renews our claim if we hold one,
   * records presence, and brings back who else is holding what.
   *
   * Failures are swallowed. A lock that could not be renewed expires, which is
   * the correct outcome, and a marker that could not be refreshed is stale for
   * a few seconds -- neither is worth an error in a person's face while they
   * are typing.
   */
  async #touch(extra: Record<string, unknown> = {}) {
    const nodeId = this.#nodeId
    if (!nodeId) return
    try {
      const out = await api<PresenceReply>(`/v1/nodes/${nodeId}/presence`, {
        method: 'POST',
        body: JSON.stringify({ ...(this.mine ? { blockId: this.mine } : {}), ...extra }),
      })
      this.held = out?.locks ?? {}
    } catch {
      /* advisory; see the note at the top */
    }
  }

  /** Take the block. Resolves once the server has been told, not before. */
  async claim(blockId: string): Promise<void> {
    if (!this.#nodeId) return
    const previous = this.mine
    this.mine = blockId
    this.#restart()
    // Release the old one in the SAME request that claims the new one, so
    // moving between paragraphs never leaves a 20-second ghost behind.
    await this.#touch(previous && previous !== blockId ? { releaseBlockId: previous } : {})
  }

  /** Give it back. Safe to call when holding nothing. */
  async release(): Promise<void> {
    const held = this.mine
    this.mine = null
    if (!held || !this.#nodeId) {
      this.#restart()
      return
    }
    this.#restart()
    await this.#touch({ releaseBlockId: held })
  }

  /** Who is in this block, if it is not us and not free. */
  holderOf(blockId: string | null | undefined): string | null {
    if (!blockId) return null
    return this.held[blockId] ?? null
  }

  stop() {
    this.#stopped = true
    if (this.#timer) clearInterval(this.#timer)
    this.#timer = null
    // Best effort on the way out: `keepalive` is the api layer's business, and
    // a lock left behind expires in 20 seconds regardless.
    void this.release()
  }
}
